import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import IceCreamCard, { LEVELS } from "@/components/IceCreamCard";
import SucursalPicker from "@/components/SucursalPicker";
import { useSucursalGate } from "@/hooks/useSucursalGate";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, IceCream2, ArrowLeft, Store, Percent } from "lucide-react";

export default function Helados() {
  const gate = useSucursalGate();
  const [items, setItems] = useState([]);
  const [levelFilter, setLevelFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async (sucursalId) => {
    if (!sucursalId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/icecreams?sucursal_id=${sucursalId}`);
      setItems(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gate.current?.id) load(gate.current.id);
    else setItems([]);
  }, [gate.current?.id]);

  const changeLevel = async (item, level) => {
    try {
      const { data } = await api.post(`/icecreams/${item.id}/set-level`, { level });
      setItems((p) => p.map((x) => (x.id === data.id ? data : x)));
      if (level <= 50 && item.stock_level > 50) {
        toast.success(`"${data.name}" en ${level}%`, {
          description: "Se notificó al Gerente.",
        });
      } else {
        toast.success(`"${data.name}" actualizado a ${level}%`);
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
      throw e;
    }
  };

  const changeReserve = async (item, count) => {
    try {
      const { data } = await api.post(`/icecreams/${item.id}/set-reserve`, { count });
      setItems((p) => p.map((x) => (x.id === data.id ? data : x)));
      toast.success(
        count > 0
          ? `"${data.name}" — reserva: ${count} bote${count === 1 ? "" : "s"}`
          : `"${data.name}" — sin reserva`
      );
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
      throw e;
    }
  };

  const useReserve = async (item) => {
    try {
      const { data } = await api.post(`/icecreams/${item.id}/use-reserve`);
      setItems((p) => p.map((x) => (x.id === data.id ? data : x)));
      toast.success(`"${data.name}" rellenado a 100%`, {
        description: `Reserva restante: ${data.reserve_count} bote${data.reserve_count === 1 ? "" : "s"}.`,
      });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
      throw e;
    }
  };

  const filteredItems = useMemo(() => {
    if (levelFilter === "all") return items;
    if (levelFilter === "low") return items.filter((it) => (it.stock_level ?? 100) <= 50);
    const lv = parseInt(levelFilter, 10);
    return items.filter((it) => (it.stock_level ?? 100) === lv);
  }, [items, levelFilter]);

  const lowCount = filteredItems.filter((i) => (i.stock_level ?? 100) <= 50).length;

  if (gate.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
      </div>
    );
  }

  if (!gate.current) {
    return (
      <SucursalPicker
        sucursales={gate.sucursales}
        onPick={gate.setCurrent}
        title="Helados"
        subtitle="¿En qué sucursal vas a revisar los sabores?"
      />
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          {gate.sucursales.length > 1 && (
            <button
              type="button"
              onClick={gate.clear}
              data-testid="change-sucursal-chip"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#8A796F] hover:text-[#FF5C5C] mb-2 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Cambiar sucursal
            </button>
          )}
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#4A3B32]" data-testid="helados-title">
            ¿Cuánto queda?
          </h1>
          <p className="text-[#8A796F] mt-1 flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5" />
            <span className="font-semibold text-[#4A3B32]">{gate.current.name}</span>
            <span className="hidden sm:inline">— toca un sabor y elige cuánto queda.</span>
          </p>
        </div>
        <div className="px-4 py-2 rounded-2xl bg-white border border-[#EADBCE] shrink-0">
          <div className="text-xs text-[#8A796F] font-semibold uppercase tracking-wide">Bajos ≤ 50%</div>
          <div className="text-2xl font-display font-extrabold text-[#FF5C5C]" data-testid="helados-low-count">
            {lowCount}
          </div>
        </div>
      </header>

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Percent className="w-4 h-4 text-[#8A796F]" />
          <Select value={levelFilter} onValueChange={setLevelFilter}>
            <SelectTrigger className="h-10 rounded-full border-[#EADBCE] bg-white w-auto min-w-[200px]" data-testid="ic-level-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los niveles</SelectItem>
              <SelectItem value="low">Bajos (≤ 50%)</SelectItem>
              {LEVELS.slice().reverse().map((l) => (
                <SelectItem key={l.value} value={String(l.value)}>
                  <span className="flex items-center gap-2">
                    <img src={l.src} alt="" className="w-5 h-5 object-contain" />
                    <span>{l.value}%</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {levelFilter !== "all" && (
            <button
              type="button"
              onClick={() => setLevelFilter("all")}
              className="text-xs font-semibold text-[#8A796F] hover:text-[#FF5C5C] px-3 py-2 rounded-full"
            >
              Limpiar filtro
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white border border-dashed border-[#EADBCE] rounded-3xl py-16 text-center" data-testid="helados-empty">
          <IceCream2 className="mx-auto w-12 h-12 text-[#EADBCE]" />
          <h3 className="mt-4 font-display font-bold text-lg text-[#4A3B32]">Sin sabores</h3>
          <p className="text-sm text-[#8A796F] mt-1">El Gerente puede agregarlos en Configuración.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6" data-testid="helados-grid">
          {filteredItems.map((it) => (
            <IceCreamCard key={it.id} item={it} onChangeLevel={changeLevel} onChangeReserve={changeReserve} onUseReserve={useReserve} />
          ))}
        </div>
      )}
    </div>
  );
}
